/**
 * Browser-side auth client for the demo deployment.
 *
 * Talks to the session API behind Caddy (same origin, /api/auth/*). The session
 * lives in an HttpOnly cookie set by the server, so the browser never sees the
 * token — every call uses `credentials: 'include'` and the cookie rides along.
 *
 * Flow:
 *   - Agents: requestMagicLink(email) → email with a one-time link →
 *             /auth?token=… → verifyMagicToken(token) → session.
 *   - Admin:  adminLogin(email, password) → session.
 *   - Restore: fetchMe() on load to rehydrate the session after a refresh.
 */
import type { Identity } from '../contexts/IdentityContext.js';

export interface ServerIdentity {
  agentId: string;
  role: 'agent' | 'admin';
  displayName?: string;
  email?: string;
}

export function toIdentity(s: ServerIdentity): Identity {
  return {
    agentId: s.agentId,
    role: s.role,
    ...(s.displayName ? { displayName: s.displayName } : {}),
  };
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

/** Always resolves with a neutral message (no account enumeration). */
export async function requestMagicLink(email: string): Promise<string> {
  try {
    const r = await postJson('/api/auth/request-link', { email });
    const data = await r.json().catch(() => ({}));
    return data.message ?? 'Si tu email está registrado, recibirás un enlace de acceso en breve.';
  } catch {
    return 'Si tu email está registrado, recibirás un enlace de acceso en breve.';
  }
}

/** Consumes a magic-link token. Throws with a user-facing message on failure. */
export async function verifyMagicToken(token: string): Promise<ServerIdentity> {
  const r = await postJson('/api/auth/verify', { token });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error ?? 'El enlace no es válido o ha caducado.');
  }
  return r.json();
}

/** Admin (password) login. Throws with a user-facing message on failure. */
export async function adminLogin(email: string, password: string): Promise<ServerIdentity> {
  const r = await postJson('/api/auth/login', { email, password });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error ?? 'Credenciales no válidas.');
  }
  return r.json();
}

/** Rehydrate the session on load. Returns null if not authenticated. */
export async function fetchMe(): Promise<ServerIdentity | null> {
  try {
    const r = await fetch('/api/auth/me', { credentials: 'include' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  try { await postJson('/api/auth/logout', {}); } catch { /* ignore */ }
}
